import { useState } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Button from '@/components/ui/Button'
import TextField from '@/components/ui/TextField'
import { signUp, useAuth } from '@/auth/useAuth'
import AuthShell from './AuthShell'

const MIN_PASSWORD_LENGTH = 8

export default function RegisterPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { session, isLoading } = useAuth()

  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false)

  if (!isLoading && session) return <Navigate to="/" replace />

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(t('auth.errors.passwordTooShort', { min: MIN_PASSWORD_LENGTH }))
      return
    }

    setIsSubmitting(true)
    try {
      const { needsEmailConfirmation } = await signUp(email, password, displayName.trim())
      if (needsEmailConfirmation) {
        setAwaitingConfirmation(true)
        return
      }
      void navigate('/', { replace: true })
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('auth.errors.registrationFailed'))
    } finally {
      setIsSubmitting(false)
    }
  }

  if (awaitingConfirmation) {
    return (
      <AuthShell title={t('auth.register.confirmTitle')}>
        <p className="text-sm">{t('auth.register.confirmBody', { email })}</p>
        <p className="text-roast-500 dark:text-crema-300/70 mt-4 text-sm">
          <Link to="/login" className="text-roast-600 dark:text-crema-200 font-medium underline">
            {t('auth.register.toLogin')}
          </Link>
        </p>
      </AuthShell>
    )
  }

  return (
    <AuthShell title={t('auth.register.title')} subtitle={t('auth.register.subtitle')}>
      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
        <TextField
          label={t('auth.fields.displayName')}
          autoComplete="nickname"
          required
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
        />
        <TextField
          label={t('auth.fields.email')}
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <TextField
          label={t('auth.fields.password')}
          type="password"
          autoComplete="new-password"
          required
          minLength={MIN_PASSWORD_LENGTH}
          hint={t('auth.register.passwordHint', { min: MIN_PASSWORD_LENGTH })}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        {error && (
          <p role="alert" className="text-over text-sm">
            {error}
          </p>
        )}

        <Button type="submit" isLoading={isSubmitting} className="w-full">
          {isSubmitting ? t('auth.register.submitting') : t('auth.register.submit')}
        </Button>
      </form>

      <p className="text-roast-500 dark:text-crema-300/70 mt-6 text-center text-sm">
        {t('auth.register.hasAccount')}{' '}
        <Link to="/login" className="text-roast-600 dark:text-crema-200 font-medium underline">
          {t('auth.register.toLogin')}
        </Link>
      </p>
    </AuthShell>
  )
}
